import {
  Box,
  Button,
  Heading,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Stack,
  useDisclosure,
} from '@chakra-ui/react';
import { createPrismaSort, DataTable, DataTableSearch, useCrud } from '@pelicargo/datatable';
import { Address } from '@pelicargo/types';
import { createColumnHelper } from '@tanstack/react-table';
import { useCallback, useMemo, useState } from 'react';
import { HiEllipsisVertical } from 'react-icons/hi2';

import { AirportFormDrawer } from '../components/Drawers/AirportFormDrawer';
import { useAirports } from '../hooks/airport/useAirports';

type TableColumns = {
  id: string;
  name: string;
  address: Address;
  action?: any;
};

export const AirportsScreen = () => {
  const airportDrawer = useDisclosure();

  const { query, handleQueryChange, sorting, setSorting, pagination, setPagination, take, skip } = useCrud();

  const [selectedAirportId, setSelectedAirportId] = useState<string>('');

  const {
    data,
    count = 0,
    refetch,
  } = useAirports({
    search: query,
    orderBy: createPrismaSort(sorting),
    skip,
    take,
  });

  const onRowClick = useCallback(
    (row: TableColumns) => {
      setSelectedAirportId(row?.id);
      airportDrawer.onOpen();
    },
    [airportDrawer],
  );

  const onCloseCallback = () => {
    setSelectedAirportId('');
    refetch();
    airportDrawer.onClose();
  };

  const columnHelper = createColumnHelper<TableColumns>();
  const columns = useMemo(
    () => [
      columnHelper.accessor('id', {
        header: 'ID',
        minSize: 0,
        size: 0,
      }),
      columnHelper.accessor('name', {
        header: 'Name',
        size: Number.MIN_SAFE_INTEGER,
      }),
      columnHelper.accessor('address', {
        header: 'Address',
        cell: (cell) => {
          const value = cell.getValue() as any;
          return `${value?.city}, ${value?.country}`;
        },
      }),
      columnHelper.accessor('action', {
        header: '',
        minSize: 0,
        size: 0,
        cell: (cell) => {
          const row = cell.row.original;
          return (
            <Box onClick={(e) => e.stopPropagation()}>
              <Menu size="xs">
                <MenuButton
                  as={IconButton}
                  aria-label="Options"
                  size="sm"
                  icon={<HiEllipsisVertical />}
                  variant="ghost"
                />
                <MenuList>
                  <MenuItem onClick={() => onRowClick(row)}>Update</MenuItem>
                </MenuList>
              </Menu>
            </Box>
          );
        },
      }),
    ],
    [columnHelper, onRowClick],
  );

  return (
    <>
      <Stack spacing="0" h="full">
        <Stack p="4">
          <Stack direction="row" align="center" justify="space-between" zIndex="99">
            <Stack direction="row" align="center" w="full" spacing="4">
              <Heading>Airports</Heading>
              <DataTableSearch value={query} onChange={handleQueryChange} />
            </Stack>
            <Button colorScheme="brand" onClick={airportDrawer.onOpen}>
              Create Airport
            </Button>
          </Stack>
        </Stack>
        <DataTable
          columns={columns}
          data={data ?? []}
          dataCount={count ?? 0}
          pagination={pagination}
          setPagination={setPagination}
          sorting={sorting}
          setSorting={setSorting}
          onRowClick={onRowClick}
        />
      </Stack>
      <AirportFormDrawer isOpen={airportDrawer.isOpen} onClose={onCloseCallback} airportId={selectedAirportId} />
    </>
  );
};
